import { useTranslation } from "react-i18next";
import type { ActionDataBo } from "@/types/tableAction";
import { FinalizeExpeditionModal } from "@/components/backOffice/pendingExpeditions/finalizeExpedition.modal";
import { DeliveryFromDateModal } from "@/components/shared/modal/deliveryFromDate.modal";
import { DeleteBoModal } from "@/components/shared/modal/delete.modal";
import { PickupDateModal } from "@/components/shared/modal/pickupDate.modal";
import { NotificationModal } from "@/components/shared/modal/notification.modal";
import { CustomQuotationQuoteBoModal } from "@/components/backOffice/pendingCustomQuotation/modal/customQuotationQuoteBo.modal";
import { ExpeditionDetailDrawer } from "@/components/backOffice/shared/expeditionDetail/expeditionDetailDrawer";
import { NotesModal } from "@/components/shared/modal/notes.modal";
import { PackagingDimensionsModal } from "@/components/backOffice/packaging/packagingDimensions.modal";
import { useFinalizeDraftRedirect } from "@/components/backOffice/pendingExpeditions/useFinalizeDraftRedirect";
import { AssignExpeditionModal } from "@/components/backOffice/pendingExpeditions/assignExpedition.modal";
import type { GetAllExpeditionBoResponseDtoExpedition } from "@/types/api/api-types";
import { ConfirmationModal } from "@/components/shared/confirmation.modal";
import { useResetDraftOwner } from "@/api/expedition.api";

type PendingExpeditionActionModalsProps = {
  actionData: ActionDataBo | null;
  setActionData: (value: ActionDataBo | null) => void;
};

export function PendingExpeditionActionModals({
  actionData,
  setActionData,
}: PendingExpeditionActionModalsProps) {
  const { t } = useTranslation("boExpeditions");
  useFinalizeDraftRedirect(actionData, setActionData);
  const { mutate: resetDraftOwner } = useResetDraftOwner();

  if (!actionData) return null;

  const onClose = () => setActionData(null);

  switch (actionData.action) {
    case "DELETE":
      return <DeleteBoModal expedition={actionData.expedition} open={true} onClose={onClose} />;
    case "FINALIZE":
    case "MODIFY":
      if (actionData.expedition.draftBy) return null;
      return (
        <FinalizeExpeditionModal expedition={actionData.expedition} open={true} onClose={onClose} />
      );
    case "DELIVERY_FROM_DATE":
      return (
        <DeliveryFromDateModal expedition={actionData.expedition} open={true} onClose={onClose} />
      );
    case "POSTPONE":
      return <PickupDateModal expedition={actionData.expedition} open={true} onClose={onClose} />;
    case "NOTIFICATION":
      return (
        <NotificationModal expeditionId={actionData.expedition.id} open={true} onClose={onClose} />
      );
    case "NOTES":
      return <NotesModal expedition={actionData.expedition} open={true} onClose={onClose} />;
    case "PACKAGING_DIMENSIONS":
      return (
        <PackagingDimensionsModal
          expedition={actionData.expedition}
          open={true}
          onClose={onClose}
        />
      );
    case "QUOTE":
      return (
        <CustomQuotationQuoteBoModal
          expeditionRow={actionData.expedition}
          open={true}
          onClose={onClose}
        />
      );
    case "DETAIL": {
      const { expedition } = actionData;
      return (
        <ExpeditionDetailDrawer
          expeditionId={expedition.id}
          companyId={expedition.companyId}
          open={true}
          onClose={onClose}
          setActionData={setActionData}
          isDeleted={false}
        />
      );
    }
    case "ASSIGN":
      return (
        <AssignExpeditionModal
          expedition={actionData.expedition as GetAllExpeditionBoResponseDtoExpedition}
          open={true}
          onClose={onClose}
        />
      );
    case "RESET_DRAFT_OWNER":
      return (
        <ConfirmationModal
          open={true}
          onClose={onClose}
          title={t("pendingActionModals.resetDraftOwnerTitle")}
          text={t("pendingActionModals.resetDraftOwnerDescription")}
          confirm={() => resetDraftOwner(actionData.expedition.id)}
        />
      );
    default:
      return null;
  }
}
